// @ts-nocheck
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeftRight,
  Bike,
  Grid3X3,
  Home,
  Package,
  ShoppingBag,
  Store,
  Ticket,
  Truck,
  UserRoundCheck,
  X,
} from 'lucide-react';

import { getVisibleBxApps, type BxAppDefinition } from '../../config/bxApps';
import { resolveAppTileHref, isExternalHref } from '../../config/railNavigation';
import { useActivePersonaMaybe } from '../../context/ActivePersonaContext';
import { useActivityCart } from '../../context/ActivityCartContext';

import css from './AppsLauncher.module.css';

const ICONS = {
  Store,
  Truck,
  Package,
  ArrowLeftRight,
  ShoppingBag,
  Home,
  Bike,
  UserRoundCheck,
  Ticket,
};

const AppTile = ({ app, onNavigate }: { app: BxAppDefinition; onNavigate: () => void }) => {
  const Icon = ICONS[app.icon] || Store;
  const href = resolveAppTileHref(app);
  const className = css.tile;
  const common = {
    className,
    onClick: onNavigate,
    'data-testid': `apps-launcher-${app.id}`,
    style: { '--app-accent': app.accent },
  };

  if (isExternalHref(href)) {
    return (
      <a href={href} rel="noopener noreferrer" {...common}>
        <span className={css.tileIcon} aria-hidden>
          <Icon size={22} />
        </span>
        <span className={css.tileName}>{app.shortName}</span>
      </a>
    );
  }

  return (
    <Link to={href} {...common}>
      <span className={css.tileIcon} aria-hidden>
        <Icon size={22} />
      </span>
      <span className={css.tileName}>{app.shortName}</span>
    </Link>
  );
};

const AppsLauncher = () => {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const personaContext = useActivePersonaMaybe();
  const { activeCount } = useActivityCart();

  const showMerchantNav = personaContext ? personaContext.showMerchantNav : true;
  const showRunnerNav = personaContext ? personaContext.showRunnerNav : true;
  const apps = getVisibleBxApps({ showMerchantNav, showRunnerNav });

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div className={css.root} ref={panelRef}>
      <button
        type="button"
        className={css.trigger}
        aria-label="BetterXchange apps"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen(prev => !prev)}
        data-testid="apps-launcher-trigger"
      >
        <Grid3X3 size={18} />
        {activeCount > 0 ? (
          <span className={css.badge} aria-label={`${activeCount} active flows`}>
            {activeCount > 9 ? '9+' : activeCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <>
          <button
            type="button"
            className={css.backdrop}
            aria-label="Close apps menu"
            onClick={() => setOpen(false)}
          />
          <div className={css.panel} role="dialog" aria-label="BetterXchange apps">
            <header className={css.panelHeader}>
              <div>
                <h2 className={css.panelTitle}>BX Apps</h2>
                <p className={css.panelSubtitle}>
                  Each app is a full end-to-end lane — save progress in Activity anytime.
                </p>
              </div>
              <button
                type="button"
                className={css.closeBtn}
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </header>

            <div className={css.grid}>
              {apps.map(app => (
                <AppTile key={app.id} app={app} onNavigate={() => setOpen(false)} />
              ))}
            </div>

            <footer className={css.panelFooter}>
              <Link to="/activity" className={css.activityLink} onClick={() => setOpen(false)}>
                Activity & order book
                {activeCount > 0 ? ` (${activeCount})` : ''}
              </Link>
            </footer>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default AppsLauncher;
